import React, { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { useLicense } from "../licensing/LicenseContext";
import { uploadLicense, reloadLicense } from "../api/licenseApi";
import { getOrganizationUsers } from "../api/orgAdminApi";
import { Hero } from "../components/adminSurface/AdminSurface";
import { LicenseArt } from "../components/adminSurface/heroArt";

export default function LicensePage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { status, loading, refresh, mode } = useLicense();

  const [rawJws, setRawJws] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);
  const [reloading, setReloading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // The license status is computed server-side, but REST's own identity store can be
  // empty/unreachable in some deployments (users are owned by the OATH service). Count
  // seats the same way the Users & Groups page does — straight from OATH in the browser —
  // and prefer that when available; fall back to the server's totalUsers otherwise.
  const [orgUserCount, setOrgUserCount] = useState(null);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    getOrganizationUsers(token)
      .then((users) => { if (!cancelled) setOrgUserCount(Array.isArray(users) ? users.length : null); })
      .catch(() => { if (!cancelled) setOrgUserCount(null); });
    return () => { cancelled = true; };
  }, [token]);

  const readFileAsText = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsText(file);
    });

  const handleFilePicked = useCallback(async (file) => {
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      setRawJws(text.trim());
      setUploadMsg(null);
    } catch {
      setUploadMsg({ type: "error", text: t("license.status.uploadError") + " Could not read file." });
    }
  }, [t]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFilePicked(file);
  }, [handleFilePicked]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const handleUpload = async () => {
    if (!rawJws.trim()) return;
    setUploading(true);
    setUploadMsg(null);
    try {
      await uploadLicense(rawJws.trim(), token);
      setUploadMsg({ type: "success", text: t("license.status.uploadSuccess") });
      setRawJws("");
      await refresh();
    } catch (err) {
      setUploadMsg({ type: "error", text: `${t("license.status.uploadError")} ${err.message}` });
    } finally {
      setUploading(false);
    }
  };

  const handleReload = async () => {
    setReloading(true);
    try {
      await reloadLicense(token);
      await refresh();
    } catch {
      // ignore
    } finally {
      setReloading(false);
    }
  };

  const modeColors = {
    Licensed: { bg: "#e6f7ee", border: "#52c41a", text: "#135200" },
    Community: { bg: "#e8f4fd", border: "#1890ff", text: "#0056b3" },
    Expired: { bg: "#fff7e6", border: "#fa8c16", text: "#ad4e00" },
    Invalid: { bg: "#fff1f0", border: "#ff4d4f", text: "#a8071a" },
    Revoked: { bg: "#fff1f0", border: "#ff4d4f", text: "#a8071a" },
    Missing: { bg: "#fff7e6", border: "#fa8c16", text: "#ad4e00" },
  };
  const modeStyle = modeColors[mode] || modeColors.Community;

  const fmt = (v) => (v == null ? "—" : v.toLocaleString());
  const fmtUnlimited = (v) => (v == null ? "∞" : v.toLocaleString());

  return (
    <>
      <div className="app-root">
        <div className="nav-strip">
          <NavBar />
        </div>
        <HeaderBar />
        <div className="page-container">
          <Hero
            title={t("license.status.title", { defaultValue: "License" })}
            subtitle={t("license.pageSubtitle", {
              defaultValue: "View and manage your Depository license.",
            })}
            art={<LicenseArt />}
          />

          {loading && (
            <p style={{ color: "#888" }}>
              {t("common.loading", { defaultValue: "Loading…" })}
            </p>
          )}

          {!loading && status && (
            <>
              {/* Tamper warning — shown when the document counter HMAC fails verification */}
              {status.documentCountTampered && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "10px 14px", marginBottom: 16, borderRadius: 6,
                  background: "#fff1f0", border: "1px solid #ff4d4f", color: "#a8071a",
                  fontSize: 13, fontWeight: 500,
                }}>
                  <span style={{ fontSize: 18 }}>⚠</span>
                  <span>
                    Document counter integrity check failed — the stored value was modified
                    outside the application. Contact your administrator.
                  </span>
                </div>
              )}

              {/* Mode badge */}
              <div
                style={{
                  display: "inline-block",
                  padding: "6px 16px",
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 14,
                  background: modeStyle.bg,
                  border: `1px solid ${modeStyle.border}`,
                  color: modeStyle.text,
                  marginBottom: 20,
                }}
              >
                {status.mode}
                {status.tier && status.tier !== "none" && ` — ${status.tier}`}
              </div>

              {/* Detail grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                  gap: 16,
                  marginBottom: 24,
                }}
              >
                <DetailCard
                  label={t("license.status.issuedTo")}
                  value={fmt(status.issuedTo)}
                />
                <DetailCard
                  label={t("license.status.expiresAt")}
                  value={
                    status.expiresAt
                      ? new Date(status.expiresAt).toLocaleDateString()
                      : "∞"
                  }
                />
                <DetailCard
                  label={t("license.status.maxUsers")}
                  value={fmtUnlimited(status.maxUsers)}
                  current={orgUserCount ?? status.totalUsers ?? 0}
                />
                <DetailCard
                  label={t("license.status.maxDocs")}
                  value={fmtUnlimited(status.maxDocumentsPerYear)}
                  current={status.thisYearDocumentCount ?? 0}
                  tampered={status.documentCountTampered}
                />
                <DetailCard
                  label={t("license.status.maxWorkers")}
                  value={fmtUnlimited(status.maxConcurrentWorkers)}
                  current={status.runningWorkers ?? 0}
                />
                <DetailCard
                  label={t("license.status.maxGatherers")}
                  value={fmtUnlimited(status.maxConcurrentGatherers)}
                  current={status.runningGatherers ?? 0}
                />
              </div>

              {/* Features */}
              {Array.isArray(status.features) && status.features.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                    {t("license.status.features")}
                  </h3>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {status.features.map((f) => (
                      <span
                        key={f}
                        style={{
                          display: "inline-block",
                          padding: "3px 10px",
                          borderRadius: 12,
                          background: "#f0f0f0",
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Error detail */}
              {status.errorDetail && (
                <p style={{ color: "#a8071a", fontSize: 13, marginBottom: 16 }}>
                  {status.errorDetail}
                </p>
              )}

              {/* Reload button */}
              <button
                onClick={handleReload}
                disabled={reloading}
                style={{
                  padding: "8px 16px",
                  fontSize: 13,
                  borderRadius: 6,
                  border: "1px solid #d9d9d9",
                  background: "#fafafa",
                  cursor: reloading ? "wait" : "pointer",
                  marginBottom: 32,
                }}
              >
                {reloading ? "…" : t("license.status.reload")}
              </button>

              {/* Upload section */}
              <div
                style={{
                  borderTop: "1px solid #e8e8e8",
                  paddingTop: 24,
                }}
              >
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                  {t("license.status.uploadTitle")}
                </h3>

                {/* Drop zone */}
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    width: "100%",
                    maxWidth: 600,
                    padding: "28px 20px",
                    borderRadius: 8,
                    border: `2px dashed ${dragOver ? "#1890ff" : "#d9d9d9"}`,
                    background: dragOver ? "#e8f4fd" : "#fafafa",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "border-color 0.2s, background 0.2s",
                    marginBottom: 12,
                    boxSizing: "border-box",
                  }}
                >
                  <div style={{ marginBottom: 6 }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                  </div>
                  <div style={{ fontSize: 13, color: "#555", fontWeight: 500 }}>
                    {t("license.status.dropFileHint", { defaultValue: "Drop a .dls file here, or click to browse" })}
                  </div>
                  <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                    {t("license.status.dropFileFormats", { defaultValue: ".dls license file" })}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".dls,.txt"
                    style={{ display: "none" }}
                    onChange={(e) => handleFilePicked(e.target.files?.[0])}
                  />
                </div>

                {/* Or paste manually */}
                <div style={{ fontSize: 12, color: "#999", marginBottom: 6 }}>
                  {t("license.status.orPaste", { defaultValue: "Or paste your license token:" })}
                </div>
                <textarea
                  value={rawJws}
                  onChange={(e) => setRawJws(e.target.value)}
                  placeholder={t("license.status.uploadPlaceholder")}
                  rows={4}
                  style={{
                    width: "100%",
                    maxWidth: 600,
                    padding: 10,
                    fontFamily: "monospace",
                    fontSize: 12,
                    borderRadius: 6,
                    border: "1px solid #d9d9d9",
                    resize: "vertical",
                    boxSizing: "border-box",
                  }}
                />
                <div style={{ marginTop: 8 }}>
                  <button
                    onClick={handleUpload}
                    disabled={uploading || !rawJws.trim()}
                    style={{
                      padding: "8px 20px",
                      fontSize: 13,
                      fontWeight: 600,
                      borderRadius: 6,
                      border: "none",
                      background: uploading || !rawJws.trim() ? "#d9d9d9" : "#1890ff",
                      color: uploading || !rawJws.trim() ? "#999" : "#fff",
                      cursor: uploading || !rawJws.trim() ? "not-allowed" : "pointer",
                    }}
                  >
                    {uploading
                      ? t("license.status.uploading")
                      : t("license.status.uploadBtn")}
                  </button>
                </div>
                {uploadMsg && (
                  <p
                    style={{
                      marginTop: 8,
                      fontSize: 13,
                      color: uploadMsg.type === "success" ? "#135200" : "#a8071a",
                    }}
                  >
                    {uploadMsg.text}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function DetailCard({ label, value, current, tampered }) {
  const showUsage = current != null;
  const borderColor = tampered ? "#ff4d4f" : "#e8e8e8";
  const currentColor = tampered ? "#a8071a" : "#1890ff";
  return (
    <div
      style={{
        padding: 14,
        borderRadius: 8,
        border: `1px solid ${borderColor}`,
        background: tampered ? "#fff1f0" : "#fafafa",
      }}
    >
      <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>
        {label}
        {tampered && <span style={{ marginLeft: 6, color: "#a8071a" }}>⚠</span>}
      </div>
      {showUsage ? (
        <div style={{ display: "flex", alignItems: "baseline", gap: 4, flexWrap: "wrap" }}>
          <span style={{ fontSize: 20, fontWeight: 700, color: currentColor }}>
            {current.toLocaleString()}
          </span>
          <span style={{ fontSize: 13, color: "#bbb" }}>/</span>
          <span style={{ fontSize: 15, fontWeight: 600, color: "#555" }}>{value}</span>
        </div>
      ) : (
        <div style={{ fontSize: 18, fontWeight: 600 }}>{value}</div>
      )}
    </div>
  );
}
